/*
import React from 'react';
import { addDays, format, startOfWeek } from 'date-fns';
import { AvailableDay, AvailableDayByDuration } from './BookingView';
import { useTranslation } from 'react-i18next';
import { he, enUS } from 'date-fns/locale';

interface CalendarViewProps {
  currentMonth: Date;
  selectedDate: Date | null;
  availableDays: (AvailableDay | AvailableDayByDuration)[];
  windowWidth: number;
  onDateSelect: (date: Date) => void;
  onNavigateMonth: (direction: 'prev' | 'next') => void;
  onContinue: () => void;
  disableNext: boolean;
  disablePrev: boolean;
}

const CalendarView: React.FC<CalendarViewProps> = ({ currentMonth, selectedDate, availableDays, windowWidth, onDateSelect, onNavigateMonth, onContinue, disableNext, disablePrev }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'he' ? he : enUS;

  const generateDays = () => {
    
    const days = [];
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const hebrewShortDays = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    const englishShortDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const daysOfWeek = Array.from({ length: 7 }).map((_, i) =>
      format(addDays(startOfWeek(new Date(), { locale }), i), 'EEE', { locale })
    );
    
    const responsiveDaysOfWeek = windowWidth < 576
      ? (i18n.language === 'he' ? hebrewShortDays : englishShortDays)
      : daysOfWeek;

    days.push(...responsiveDaysOfWeek.map((day, index) => (
      <div key={`${day}-${index}`} className="text-center fw-bold py-2 border-bottom calendar-day-header">
        {day}
      </div>
    )));

    const firstDayOfMonth = startOfMonth.getDay();
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day-empty border text-muted" />);
    }

    for (let day = 1; day <= endOfMonth.getDate(); day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isAvailable = availableDays.some(d =>
        d.date.getDate() === day &&
        d.date.getMonth() === currentMonth.getMonth() &&
        d.date.getFullYear() === currentMonth.getFullYear() &&
        d.available
      );

      const isSelected = selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === date.getMonth() &&
        selectedDate.getFullYear() === date.getFullYear();

      const today = new Date();
      const isToday =
        today.getDate() === date.getDate() &&
        today.getMonth() === date.getMonth() &&
        today.getFullYear() === date.getFullYear();

      days.push(
        <div
          key={day}
          className={`p-2 border text-center calendar-day
            ${isAvailable ? 'cursor-pointer' : 'text-muted'}
            ${isSelected ? 'bg-primary text-white' : ''}
            ${isToday ? 'calendar-today' : ''}`}
          onClick={() => isAvailable && onDateSelect(date)}
        >
          {day}
        </div>
      );

    }

    return days;
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between mb-3">
        <button className="btn btn-outline-primary btn-sm" onClick={() => onNavigateMonth('prev')} disabled={disablePrev}>{t('prev')}</button>
        <strong>{format(currentMonth, 'MMMM yyyy', { locale })}</strong>
        <button className="btn btn-outline-primary btn-sm" onClick={() => onNavigateMonth('next')} disabled={disableNext}>{t('next')}</button>
      </div>
      <div className="calendar-grid mb-3">{generateDays()}</div>
      <button className="btn btn-primary float-end mb-3" onClick={onContinue} disabled={!selectedDate}>{t('continue')}</button>
    </div>
  );
};

export default CalendarView;
*/

import React from 'react';
import { addDays, format, startOfWeek } from 'date-fns';
import { AvailableDay, AvailableDayByDuration } from './BookingView';
import { useTranslation } from 'react-i18next';
import { he, enUS } from 'date-fns/locale';
import { Container, Row, Col, Button } from 'react-bootstrap';

interface CalendarViewProps {
  currentMonth: Date;
  selectedDate: Date | null;
  availableDays: (AvailableDay | AvailableDayByDuration)[];
  windowWidth: number;
  onDateSelect: (date: Date) => void;
  onNavigateMonth: (direction: 'prev' | 'next') => void;
  onContinue: () => void;
  disableNext: boolean;
  disablePrev: boolean;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  currentMonth,
  selectedDate,
  availableDays,
  windowWidth,
  onDateSelect,
  onNavigateMonth,
  onContinue,
  disableNext,
  disablePrev,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'he' ? he : enUS;

  const generateDays = () => {
    const days: JSX.Element[] = [];
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const hebrewShortDays = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    const englishShortDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const daysOfWeek = Array.from({ length: 7 }).map((_, i) =>
      format(addDays(startOfWeek(new Date(), { locale }), i), 'EEE', { locale })
    );

    const responsiveDaysOfWeek =
      windowWidth < 576 ? (i18n.language === 'he' ? hebrewShortDays : englishShortDays) : daysOfWeek;

    // Header row (days names)
    responsiveDaysOfWeek.forEach((day, index) => {
      days.push(
        <Col
          key={`day-header-${index}`}
          className="text-center fw-bold py-2 border-bottom calendar-day-header"
        >
          {day}
        </Col>
      );
    });

    // Empty slots before the first day of the month
    const firstDayOfMonth = startOfMonth.getDay();
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Col key={`empty-${i}`} className="calendar-day-empty border text-muted" />);
    }

    // Days of month
    for (let day = 1; day <= endOfMonth.getDate(); day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

      const isAvailable = availableDays.some(
        (d) =>
          d.date.getDate() === day &&
          d.date.getMonth() === currentMonth.getMonth() &&
          d.date.getFullYear() === currentMonth.getFullYear() &&
          d.available
      );

      const isSelected =
        selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === date.getMonth() &&
        selectedDate.getFullYear() === date.getFullYear();

      const today = new Date();
      const isToday =
        today.getDate() === date.getDate() &&
        today.getMonth() === date.getMonth() &&
        today.getFullYear() === date.getFullYear();

      days.push(
        <Col
          key={`day-${day}`}
          className={`p-2 border text-center calendar-day 
            ${isAvailable ? 'cursor-pointer hover:bg-light' : 'text-muted'} 
            ${isSelected ? 'bg-primary text-white' : ''} 
            ${isToday ? 'calendar-today' : ''}`}
          onClick={() => isAvailable && onDateSelect(date)}
        >
          {day}
        </Col>
      );
    }

    return days;
  };

  return (
    <Container fluid className="p-4 appointment-customer-view">
      {/* Navigation */}
      <Row className="justify-content-between mb-3">
        <Col xs="auto">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onNavigateMonth('prev')}
            disabled={disablePrev}
          >
            {t('prev')}
          </Button>
        </Col>
        <Col xs="auto" className="fw-bold">
          {format(currentMonth, 'MMMM yyyy', { locale })}
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onNavigateMonth('next')}
            disabled={disableNext}
          >
            {t('next')}
          </Button>
        </Col>
      </Row>

      {/* Calendar Grid */}
      <Row xs={7} className="calendar-grid mb-3">
        {generateDays()}
      </Row>

      {/* Continue Button */}
      <Row>
        <Col className="text-end">
          <Button variant="primary" className="mb-3" onClick={onContinue} disabled={!selectedDate}>
            {t('continue')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default CalendarView;
